import { useEffect, useRef, useState } from "react";
import type { ApiResponse, JournalEntry, LocationVisit } from "@smrti/types";
import { PlaceSuggestions } from "./PlaceSuggestions";
import { recoveredVisit, VisitPlaceEditor } from "./VisitPlaceEditor";
import { DayCalendar } from "./DayCalendar";
import { recoveredTimeVisit, VisitTimeEditor } from "./VisitTimeEditor";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { journalKey, visitsKey } from "./JournalQueryProvider";

// YYYY-MM-DD for the local timezone (not toISOString — that's UTC).
export function localDateISO(d = new Date()): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// Build [from, to) as UTC instants representing the local-time day boundaries
// for the given YYYY-MM-DD. The Date constructor parses "YYYY-MM-DD" in
// local time when used via the multi-arg form.
function dayBounds(dateStr: string): { from: string; to: string } {
  const [y, m, d] = dateStr.split("-").map(Number);
  const from = new Date(y, m - 1, d, 0, 0, 0, 0);
  const to = new Date(y, m - 1, d + 1, 0, 0, 0, 0);
  return { from: from.toISOString(), to: to.toISOString() };
}

function shiftDay(dateStr: string, delta: number): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d + delta);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatClock(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

// With overlap semantics a visit's arrival or departure can land outside the
// viewed day (it spanned midnight, is a multi-day stay, or is still ongoing).
// Show the time of day when the instant is within the viewed window, otherwise
// prefix the date so "8:00 AM" can't be misread as today.
function formatBoundary(iso: string, viewStart: number, viewEnd: number): string {
  const d = new Date(iso);
  const ms = d.getTime();
  if (ms >= viewStart && ms < viewEnd) return formatClock(d);
  return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}, ${formatClock(d)}`;
}

function formatDuration(arrival: string | null, departure: string | null): string | null {
  if (!arrival || !departure) return null;
  const ms = new Date(departure).getTime() - new Date(arrival).getTime();
  if (ms <= 0) return null;
  const totalMins = Math.round(ms / 60000);
  // A positive but sub-30s visit rounds to 0; don't render a "0m" stay.
  if (totalMins === 0) return "<1m";
  const days = Math.floor(totalMins / 1440);
  const hours = Math.floor((totalMins % 1440) / 60);
  const mins = totalMins % 60;
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  return `${mins}m`;
}

function visitLabel(v: LocationVisit): string {
  if (v.placeName) return v.placeName;
  // A GPS-recovered stay has no geocode lifecycle — no label is coming until
  // place resolution (SMR-51). Say what it is instead of "Unknown place".
  if (v.source === "gps") return "Detected stay";
  if (v.geocodeStatus === "pending") return "Locating…";
  if (v.geocodeStatus === "no_result" || v.geocodeStatus === "gave_up") return "Unknown place";
  if (v.geocodeStatus === "failed") return "Geocode failed";
  return `${v.latitude.toFixed(4)}, ${v.longitude.toFixed(4)}`;
}

export function DayView({
  apiBase,
  userId,
  date,
  onDateChange,
  onNew,
  onOpen,
  onSignOut,
}: {
  apiBase: string;
  userId: string;
  date: string;
  onDateChange: (date: string) => void;
  onNew: () => void;
  onOpen: (entry: JournalEntry) => void;
  onSignOut?: () => void;
}) {
  const queryClient = useQueryClient();
  const journals = useQuery({
    queryKey: journalKey(apiBase, userId),
    queryFn: async ({ signal }) => {
      const response = await fetch(`${apiBase}/api/entries`, { credentials: "include", signal });
      const body = (await response.json()) as ApiResponse<JournalEntry[]>;
      if (!response.ok || !body.ok) throw new Error("Couldn’t load journal entries.");
      return body.data;
    },
  });
  const { from, to } = dayBounds(date);
  const visitQuery = useQuery({
    queryKey: [...visitsKey(apiBase, userId), from, to],
    queryFn: async ({ signal }) => {
      const url = `${apiBase}/api/location/visits?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
      const response = await fetch(url, { credentials: "include", signal });
      const body = (await response.json()) as ApiResponse<LocationVisit[]>;
      if (!response.ok || !body.ok) throw new Error("Couldn’t load visits.");
      return body.data;
    },
  });
  const refreshVisits = () => {
    void queryClient.invalidateQueries({ queryKey: visitsKey(apiBase, userId) });
  };
  const visits = visitQuery.data ?? [];
  const entries = journals.data ?? [];
  const loading = visitQuery.isPending;
  const error = visitQuery.error;
  const journalLoading = journals.isPending;
  const journalError = journals.error?.message;
  const [editingVisit, setEditingVisit] = useState(() => recoveredVisit(userId));
  const [editingTime, setEditingTime] = useState(() => recoveredTimeVisit(userId));
  const [calendarOpen, setCalendarOpen] = useState(false);
  const calendarToggle = useRef<HTMLButtonElement>(null);
  const dayHeading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (!editingVisit && !editingTime) dayHeading.current?.focus();
  }, [editingVisit, editingTime]);

  const isToday = date === localDateISO();

  // Local-day window in epoch ms, used to decide whether a visit's
  // arrival/departure falls on the viewed day or spilled onto another.
  const viewStart = Date.parse(from);
  const viewEnd = Date.parse(to);
  const items = [
    ...(!journalLoading && !journalError ? entries : [])
      .filter(
        (entry) => Date.parse(entry.createdAt) >= viewStart && Date.parse(entry.createdAt) < viewEnd
      )
      .map((entry) => ({
        key: `journal-${entry.id}`,
        time: Date.parse(entry.createdAt),
        entry,
        visit: null,
      })),
    ...(!loading && !error ? visits : []).map((visit) => ({
      key: `visit-${visit.id}`,
      time: visit.arrival ? Date.parse(visit.arrival) : NaN,
      entry: null,
      visit,
    })),
  ].sort(
    (a, b) =>
      (Number.isFinite(a.time) ? a.time : Infinity) -
        (Number.isFinite(b.time) ? b.time : Infinity) || a.key.localeCompare(b.key)
  );
  const chapters = ["Earlier / overnight", "Morning", "Afternoon", "Evening", "Time unknown"];
  const chapter = (time: number) =>
    !Number.isFinite(time)
      ? "Time unknown"
      : time < viewStart
        ? "Earlier / overnight"
        : new Date(time).getHours() < 12
          ? "Morning"
          : new Date(time).getHours() < 18
            ? "Afternoon"
            : "Evening";

  return (
    <div className="day-layout">
      {editingTime && !editingVisit && (
        <VisitTimeEditor
          key={`${userId}:${editingTime.visitId ?? "missing"}`}
          userId={userId}
          apiBase={apiBase}
          visitId={editingTime.visitId}
          visit={visits.find((v) => v.id === editingTime.visitId)}
          onSignOut={onSignOut}
          onClose={() => {
            setEditingTime(null);
            refreshVisits();
          }}
          onChanged={refreshVisits}
        />
      )}
      {editingVisit && (
        <VisitPlaceEditor
          key={`${userId}:${editingVisit}`}
          userId={userId}
          apiBase={apiBase}
          visitId={editingVisit}
          onSignOut={onSignOut}
          onClose={() => {
            setEditingVisit(null);
            refreshVisits();
          }}
          onChanged={refreshVisits}
        />
      )}
      <aside className="day-navigation">
        <h2 tabIndex={-1} className="font-semibold text-teal-800">
          Days
        </h2>
        <button
          ref={calendarToggle}
          className="calendar-toggle"
          aria-expanded={calendarOpen}
          aria-controls="day-calendar-panel"
          onClick={() => setCalendarOpen(!calendarOpen)}
        >
          Choose a day <span aria-hidden="true">⌄</span>
        </button>
        <div
          id="day-calendar-panel"
          className={calendarOpen ? "calendar-panel is-open" : "calendar-panel"}
        >
          <DayCalendar
            date={date}
            onDateChange={(next) => {
              onDateChange(next);
              setCalendarOpen(false);
              if (calendarOpen) calendarToggle.current?.focus();
            }}
          />
          <details className="date-jump">
            <summary>Jump to a date</summary>
            <label className="block text-sm text-stone-600" htmlFor="viewed-day">
              Choose a day
            </label>
            <input
              id="viewed-day"
              type="date"
              value={date}
              max={localDateISO()}
              className="w-full rounded border border-stone-300 bg-white p-2"
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d{4}-\d{2}-\d{2}$/.test(value) && value <= localDateISO())
                  onDateChange(value);
                else e.currentTarget.value = date;
              }}
            />
          </details>
        </div>
        <p className="day-timezone">Times in {Intl.DateTimeFormat().resolvedOptions().timeZone}.</p>
      </aside>
      <div className="day-content">
        <header className="day-heading">
          <div>
            <p className="day-eyebrow">Your days, remembered</p>
            <h2 ref={dayHeading} tabIndex={-1}>
              {new Date(viewStart).toLocaleDateString(undefined, {
                weekday: "long",
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </h2>
          </div>
          <div className="day-actions">
            <button aria-label="Previous day" onClick={() => onDateChange(shiftDay(date, -1))}>
              ‹
            </button>
            <button
              aria-label="Next day"
              disabled={isToday}
              onClick={() => onDateChange(shiftDay(date, 1))}
            >
              ›
            </button>
            <button className="rounded bg-teal-800 text-white px-4" onClick={onNew}>
              + New entry
            </button>
            <button onClick={() => setEditingTime({ visitId: null })}>Add missing stay</button>
          </div>
        </header>
        <div className="day-body">
          <div className="day-timeline">
            <div aria-live="polite" className="space-y-2 text-sm text-stone-600">
              {loading && <p>Loading visits…</p>}
              {journalLoading && <p>Loading journal entries…</p>}
              {error && (
                <p role="alert">
                  Couldn’t load visits.{" "}
                  <button onClick={() => void visitQuery.refetch()}>Retry visits</button>
                </p>
              )}
              {journalError && (
                <p role="alert">
                  {journalError}{" "}
                  <button onClick={() => void journals.refetch()}>Retry journals</button>
                </p>
              )}
              {!loading && !journalLoading && !error && !journalError && items.length === 0 && (
                <p className="py-12">
                  No visits or journal entries for this day. Captures may still be processing.
                </p>
              )}
            </div>
            {chapters.map((name) => {
              const rows = items.filter((item) => chapter(item.time) === name);
              return (
                rows.length > 0 && (
                  <section key={name} aria-label={name} className="day-chapter">
                    <h3>
                      <span className="chapter-mark" aria-hidden="true">
                        {name === "Evening" ? "☾" : "☼"}
                      </span>
                      {name}
                    </h3>
                    <ol className="chapter-events">
                      {rows.map(({ key, entry, visit }) => (
                        <li key={key} className="day-event">
                          <span className="event-mark" aria-hidden="true">
                            {entry ? "≡" : "⌖"}
                          </span>
                          {entry ? (
                            <button
                              className="journal-event text-left w-full"
                              onClick={() => onOpen(entry)}
                            >
                              <span className="text-xs text-teal-800">Journal</span>
                              <h4 className="font-medium break-words">{entry.title}</h4>
                              <p className="text-sm text-stone-600">
                                Written at {formatClock(new Date(entry.createdAt))}
                                {entry.mood ? ` · ${entry.mood}` : ""}
                              </p>
                              <p className="mt-2 line-clamp-2 whitespace-pre-wrap break-words">
                                {entry.content}
                              </p>
                            </button>
                          ) : (
                            visit && (
                              <div className="visit-event">
                                <span className="text-xs text-teal-800">Visit</span>
                                <h4 className="font-medium break-words">{visitLabel(visit)}</h4>
                                <p className="text-sm text-stone-600">
                                  {visit.arrival
                                    ? formatBoundary(visit.arrival, viewStart, viewEnd)
                                    : "Unknown arrival"}{" "}
                                  —{" "}
                                  {visit.departure
                                    ? formatBoundary(visit.departure, viewStart, viewEnd)
                                    : "Departure not recorded"}{" "}
                                  ·{" "}
                                  {visit.reportedTimeAccuracy
                                    ? `Reported ${visit.reportedTimeAccuracy} times`
                                    : "Estimated times"}
                                </p>
                                <details
                                  key={`${date}:${visit.id}`}
                                  name="selected-stop"
                                  className="visit-disclosure"
                                >
                                  <summary>
                                    <span className="stop-open-label">Open visit details →</span>
                                    <span className="stop-selected-label">
                                      Selected · Close details
                                    </span>
                                  </summary>
                                  <div className="stop-rail">
                                    <p className="day-eyebrow">Selected stop</p>
                                    <h3>{visitLabel(visit)}</h3>
                                    {visit.formattedAddress && <p>{visit.formattedAddress}</p>}
                                    {formatDuration(visit.arrival, visit.departure) && (
                                      <p>
                                        {formatDuration(visit.arrival, visit.departure)} total stay
                                      </p>
                                    )}
                                    <button
                                      className="stop-change"
                                      onClick={() => setEditingVisit(visit.id)}
                                    >
                                      Change this visit’s place <span aria-hidden="true">→</span>
                                    </button>
                                    <button
                                      className="stop-change"
                                      onClick={() => setEditingTime({ visitId: visit.id })}
                                    >
                                      Edit this visit’s times <span aria-hidden="true">→</span>
                                    </button>
                                    <details className="stop-evidence">
                                      <summary>About this visit</summary>
                                      <p>
                                        Source:{" "}
                                        {visit.source === "manual"
                                          ? "Reported stay"
                                          : visit.source === "gps"
                                            ? "GPS observations"
                                            : "Apple visit observation"}
                                      </p>
                                      <p>
                                        {visit.source === "manual"
                                          ? "Saved place coordinates"
                                          : "Visit coordinates"}
                                        : {visit.latitude.toFixed(4)}, {visit.longitude.toFixed(4)}
                                      </p>
                                      {visit.source === "manual" ? (
                                        <p>Saved-place geometry, not a sensor measurement.</p>
                                      ) : (
                                        <>
                                          {visit.source === "clvisit" && (
                                            <p>
                                              Reported horizontal accuracy:{" "}
                                              {visit.horizontalAccuracy >= 0
                                                ? `${visit.horizontalAccuracy} m`
                                                : "Unavailable"}
                                            </p>
                                          )}
                                          {visit.positionEvidence && (
                                            <p>
                                              Accepted observation groups:{" "}
                                              {visit.positionEvidence.acceptedBinCount}. Largest
                                              reported group accuracy:{" "}
                                              {visit.positionEvidence.maxBinReportedAccuracyM} m.
                                              Recorded support:{" "}
                                              {(visit.positionEvidence.supportMs / 60000).toFixed(
                                                1
                                              )}{" "}
                                              min; gaps bridged:{" "}
                                              {(
                                                visit.positionEvidence.bridgedGapMs / 60000
                                              ).toFixed(1)}{" "}
                                              min.
                                            </p>
                                          )}
                                          <p>
                                            Accuracy describes location uncertainty, not a
                                            guaranteed boundary or proof of being inside.
                                          </p>
                                        </>
                                      )}
                                      <p>
                                        Curated visits are not confirmation of place or exact times.
                                      </p>
                                    </details>
                                  </div>
                                </details>
                              </div>
                            )
                          )}
                        </li>
                      ))}
                    </ol>
                  </section>
                )
              );
            })}
            <details className="border-t border-stone-200 py-4">
              <summary className="text-sm text-stone-600">Home & work suggestions</summary>
              {!editingVisit && !editingTime && (
                <PlaceSuggestions userId={userId} apiBase={apiBase} onConfirmed={refreshVisits} />
              )}
            </details>
          </div>
          <aside className="stop-placeholder" aria-label="Visit selection">
            <span className="event-mark" aria-hidden="true">
              ⌖
            </span>
            <h3>A closer look</h3>
            <p>Open a visit’s details to see its recorded information or change its place.</p>
            <p>Your journal entries stay separate, in your own words.</p>
          </aside>
        </div>
      </div>
    </div>
  );
}
